import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useChatAnalytics } from 'app/analytics';
import { off, on, useSocketContext } from 'app/socket';
import { isAudio } from 'shared_DEPRECATED/components/File/utils';
import { ReactionActionType } from 'shared_DEPRECATED/components/Reactions/types';
import { Nullable } from 'shared_DEPRECATED/types';
import { Attachment } from 'shared_DEPRECATED/types/Attachment';
import { dateUtils } from 'shared_DEPRECATED/utils';
import { LinkPreview } from 'types/common';

import { CHAT_MESSAGE_TYPES, MAX_MESSAGE_LENGTH } from 'features/chat/config';
import { UnuploadedAttachment } from 'features/chat/config/types/Attachment';
import {
  EVENT_TYPE,
  IncomingMessage,
  IncomingMessageReaction,
  Message,
  MESSAGE_STATUS,
  MessageAttachmentProcessed,
  MessageCreated,
  MessageNotification,
  MessageReceivedByServer,
  OUTGOING_EVENT_TYPE,
  UUID,
  OutgoingMessageReaction,
  ChatMessageEdited,
  BaseMessage,
  ChatMessageMorpheusSuggestion,
} from 'features/chat/config/types/Message';
import {
  SendMessage,
  ChatMessagesContextType,
  SendMessageWithAttachment,
  SendMorpheusMessage,
} from 'features/chat/config/types/types';
import { useChatInterlocutorContext } from 'features/chat/context/chatInterlocutor';
import { useChatMessageDelete } from 'features/chat/hooks/mutation/useChatMessageDelete';
import { useChatMessageEditMutation } from 'features/chat/hooks/mutation/useChatMessageEdit';
import { useChatMessageScheduleEdit } from 'features/chat/hooks/mutation/useChatMessageScheduleEdit';
import { useChatMessagesQuery } from 'features/chat/hooks/query/useChatMessages';
import { useChatMessagesActions } from 'features/chat/hooks/useChatMessagesActions';
import { scrollToBottom, splitMessage } from 'features/chat/utils';
import {
  updateMessage,
  updateMessageReactions,
} from 'features/chat/utils/messageUpdaters';
import { useUserContext } from 'features/user/context';

import { URL_REGEX } from 'shared/config/regularExpressions';
import { formatDuration } from 'shared/utils/formatDuration';
import { isMediaFile } from 'shared/utils/mediaFile';
import { getUlidId, getUuId } from 'shared/utils/uuid';

import { EditMessageNullable } from '../config/types/ParentMessage';
import { useChatContext } from './chat';
import { useChatMessageSchedulingContext } from './chatMessageScheduling';

const ChatMessagesContext = createContext<ChatMessagesContextType | undefined>(
  undefined
);

type ChatMessagesProviderPropTypes = {
  children: React.ReactNode;
};

export const ChatMessagesProvider = ({
  children,
}: ChatMessagesProviderPropTypes) => {
  const {
    sendChatMessageSentAnalyticsEvent,
    sendChatReactionAddedAnalyticsEvent,
    sendChatMessageEditedAnalyticsEvent,
    sendChatMessageDeletedAnalyticsEvent,
  } = useChatAnalytics();

  const { handleScheduledMessageSent, updateScheduledMessageProcessingStatus } =
    useChatMessageSchedulingContext();
  const chatRef = useRef<HTMLDivElement>(null);
  const chatInterlocutor = useChatInterlocutorContext();
  const { chatId } = chatInterlocutor;
  const { lastReadMessageId, setUnreadMessagesCount } = useChatContext();
  const { emit, isSocketReady } = useSocketContext();
  const { user } = useUserContext();

  const { parentMessage, setParentMessage, editedMessage, setEditedMessage } =
    useChatMessagesActions();

  const [draftMessageId, setDraftMessageId] = useState<Nullable<string>>(null);

  const { mutateAsync: editChatMessage } = useChatMessageEditMutation();
  const { mutateAsync: deleteChatMessage } = useChatMessageDelete();
  const { mutateAsync: editScheduledMessage } = useChatMessageScheduleEdit();

  const {
    fetchNextPage,
    hasNextPage,
    isFetching,
    hasFailedMessages,
    messages = [],
    setNewMessages,
    addNewMessage,
    isLoading,
  } = useChatMessagesQuery({
    chatId,
    isSocketReady,
  });

  const handleChatMessageReceivedByServer = useCallback(
    async (message: MessageReceivedByServer) => {
      setNewMessages((prev) =>
        updateMessage(prev, (msg) =>
          msg.event === OUTGOING_EVENT_TYPE.CHAT_MESSAGE_CREATE &&
          msg.temporaryMessageId === message.temporaryMessageId
            ? { ...message, temporaryMessageId: undefined }
            : null
        )
      );
    },
    [setNewMessages]
  );

  const handleChatMessageCreated = useCallback(
    (message: MessageCreated) => {
      addNewMessage(message);
      if (message.scheduledMessageId) {
        handleScheduledMessageSent(message);
      }

      // only show new messages indicator if it's not a scheduled message
      if (!message.scheduledMessageId) {
        setUnreadMessagesCount(1);
      }
    },
    [addNewMessage, setUnreadMessagesCount, handleScheduledMessageSent]
  );

  const handleMorpheusSuggestions = useCallback(
    (message: ChatMessageMorpheusSuggestion) => {
      // Handle Morpheus suggestions here

      const newMessage = {
        event: EVENT_TYPE.CHAT_MESSAGE_MORPHEUS_SUGGESTIONS,
        chatId: chatId,
        messageId: getUuId(),
        type: CHAT_MESSAGE_TYPES.MORPHEUS_SUGGESTION,
        content: {
          type: CHAT_MESSAGE_TYPES.MORPHEUS_SUGGESTION,
          suggestions: message.suggestions.map((suggestion) => ({
            ...suggestion,
            suggestionId: getUuId(),
          })),
          suggestionForMessageId: message.messageId,
        },
        text: '',
        replyTo: null,
        replyToMessage: null,
        deleted: false,
        createdAt: dateUtils.utc().toISOString(),
        status: MESSAGE_STATUS.SENT,
        reactions: {},
        fromUserId: 'morpheus-system',
      };
      //@ts-ignore
      addNewMessage(newMessage);
    },
    [chatId, addNewMessage]
  );

  const handleChatMessageReadUpdate = useCallback(
    (message: MessageNotification) => {
      setNewMessages((prev) =>
        updateMessage(prev, (msg) =>
          msg.messageId === message.messageId
            ? ({ ...msg, status: MESSAGE_STATUS.READ } as Message)
            : null
        )
      );
    },
    [setNewMessages]
  );

  const handleChatMessageEdited = useCallback(
    (message: ChatMessageEdited) => {
      setNewMessages((prev) =>
        updateMessage(prev, (msg) =>
          msg.messageId === message.messageId
            ? ({
                ...msg,
                content: {
                  ...message.content,
                },
                deleted: message.deleted,
                text: message.text,
                editedAt: message.editedAt,
                type: message.type,
              } as Message)
            : null
        )
      );
    },
    [setNewMessages]
  );

  const handleChatMessageReactionAdded = useCallback(
    (message: IncomingMessageReaction | OutgoingMessageReaction) => {
      setNewMessages((prev) => {
        return updateMessageReactions({
          prevMessages: prev,
          messageId: message.messageId,
          reaction: message.reaction,
          action: 'ADD',
          userInfo:
            'userId' in message && message.userId !== user.userId
              ? chatInterlocutor
              : { ...user, chatId: chatId },
        });
      });
    },
    [user, setNewMessages, chatId, chatInterlocutor]
  );

  const handleChatMessageReactionRemoved = useCallback(
    (message: IncomingMessageReaction | OutgoingMessageReaction) => {
      setNewMessages((prev) => {
        return updateMessageReactions({
          prevMessages: prev,
          messageId: message.messageId,
          reaction: message.reaction,
          action: 'REMOVE',
          userInfo:
            'userId' in message && message.userId !== user.userId
              ? chatInterlocutor
              : { ...user, chatId: chatId },
        });
      });
    },
    [user, setNewMessages, chatId, chatInterlocutor]
  );

  const handleChatMessageFailedUpload = useCallback(
    (temporaryMessageId: string) => {
      setNewMessages((prev) =>
        updateMessage(prev, (msg) =>
          temporaryMessageId === msg.temporaryMessageId
            ? ({ ...msg, status: MESSAGE_STATUS.FAILED } as Message)
            : null
        )
      );
    },
    [setNewMessages]
  );

  const handleChatMessageAttachmentProcessed = useCallback(
    (message: MessageAttachmentProcessed) => {
      if (message.isScheduledMessage) {
        updateScheduledMessageProcessingStatus(message);
      } else {
        setNewMessages((prev) =>
          updateMessage(prev, (msg) => {
            const isCurrentMessage = msg.messageId === message.messageId;
            if (!isCurrentMessage || !msg.attachments) {
              return null;
            }

            const newAttachments = msg.attachments.map((attachment) => {
              const attachmentId =
                'attachmentId' in attachment ? attachment.attachmentId : '';
              return attachmentId === message.attachmentId
                ? {
                    ...attachment,
                    processingStatus: message.processingStatus,
                    url: `${attachment.url}?ts=${Date.now()}`,
                  }
                : attachment;
            });

            return {
              ...msg,
              attachments: newAttachments,
            } as Message;
          })
        );
      }
    },
    [setNewMessages, updateScheduledMessageProcessingStatus]
  );

  const handleIncomingMessages = useCallback(
    (message: IncomingMessage) => {
      switch (message.event) {
        case EVENT_TYPE.CHAT_MESSAGE_RECEIVED_BY_SERVER:
          handleChatMessageReceivedByServer(message);
          break;
        case EVENT_TYPE.CHAT_MESSAGE_CREATED:
          handleChatMessageCreated(message);
          break;
        case EVENT_TYPE.CHAT_MESSAGE_READ_UPDATE:
          handleChatMessageReadUpdate(message);
          break;
        case EVENT_TYPE.CHAT_MESSAGE_EDITED:
          handleChatMessageEdited(message);
          break;
        case EVENT_TYPE.CHAT_MESSAGE_REACTION_ADDED:
        case EVENT_TYPE.CHAT_MESSAGE_REACTION_ADDED_BY_INTERLOCUTOR:
          handleChatMessageReactionAdded(message);
          break;
        case EVENT_TYPE.CHAT_MESSAGE_REACTION_REMOVED:
        case EVENT_TYPE.CHAT_MESSAGE_REACTION_REMOVED_BY_INTERLOCUTOR:
          handleChatMessageReactionRemoved(message);
          break;
        case EVENT_TYPE.CHAT_MESSAGE_ATTACHMENT_PROCESSED:
          handleChatMessageAttachmentProcessed(message);
          break;
        case EVENT_TYPE.CHAT_MESSAGE_MORPHEUS_SUGGESTIONS:
          handleMorpheusSuggestions(message);
          break;
        default:
          break;
      }
    },
    [
      handleChatMessageReceivedByServer,
      handleChatMessageCreated,
      handleChatMessageReadUpdate,
      handleChatMessageReactionAdded,
      handleChatMessageReactionRemoved,
      handleChatMessageAttachmentProcessed,
      handleChatMessageEdited,
      handleMorpheusSuggestions,
    ]
  );

  const createNewMessage = useCallback(
    (messageText: string, linkPreview?: LinkPreview) => {
      const temporaryMessageId = getUlidId();

      emit({
        event: OUTGOING_EVENT_TYPE.CHAT_MESSAGE_CREATE,
        chat_id: chatId,
        text: messageText,
        temporary_message_id: temporaryMessageId,
        reply_to: parentMessage?.messageId || null,
        fromUserId: user.userId,
        linkPreview: linkPreview || null,
        ...(draftMessageId
          ? {
              metadata: { draft_message_id: draftMessageId },
            }
          : {}),
      });

      sendChatMessageSentAnalyticsEvent({
        includesAttachments: false,
        includesMedia: false,
        includesLink: URL_REGEX.test(messageText),
        includesReply: !!parentMessage,
        includesAudio: false,
        audioLength: '',
      });

      addNewMessage({
        event: OUTGOING_EVENT_TYPE.CHAT_MESSAGE_CREATE,
        chatId,
        messageId: temporaryMessageId,
        temporaryMessageId: temporaryMessageId,
        text: messageText,
        replyTo: parentMessage?.messageId || null,
        replyToMessage: parentMessage,
        createdAt: dateUtils.utc().toISOString(),
        status: MESSAGE_STATUS.CREATED,
        reactions: {},
        fromUserId: user.userId,
        deleted: false,
        type: CHAT_MESSAGE_TYPES.PLAIN,
        content: {
          type: CHAT_MESSAGE_TYPES.PLAIN,
          text: messageText,
          attachments: [],
          linkPreview: linkPreview || null,
        },
      });
    },
    [
      chatId,
      user,
      parentMessage,
      emit,
      addNewMessage,
      sendChatMessageSentAnalyticsEvent,
      draftMessageId,
    ]
  );

  const editMessage = useCallback(
    async (
      messageText: string,
      linkPreview?: LinkPreview,
      attachments?: UnuploadedAttachment[]
    ) => {
      setNewMessages((prev) =>
        updateMessage(prev, (msg: BaseMessage) =>
          editedMessage && msg.messageId === editedMessage.messageId
            ? ({
                ...msg,
                text: messageText,
                attachments: attachments || editedMessage.attachments || [],
                editedAt: dateUtils().toISOString(),
                content: {
                  ...msg.content,
                  linkPreview: linkPreview || null,
                },
              } as Message)
            : null
        )
      );

      sendChatMessageEditedAnalyticsEvent();

      if (editedMessage?.scheduledFor) {
        await editScheduledMessage({
          text: messageText,
          messageId: editedMessage.messageId,
          replyTo: editedMessage.replyTo!,
          attachments: editedMessage.attachments as Attachment[],
          scheduledFor: editedMessage.scheduledFor,
          linkPreview: linkPreview ?? null,
        });
      } else {
        await editChatMessage({
          text: messageText,
          messageId: editedMessage?.messageId as UUID,
          replyTo: editedMessage?.replyTo!,
          attachments: editedMessage?.attachments as Attachment[],
          linkPreview: linkPreview ?? null,
        });
      }

      scrollToBottom(chatRef?.current);
    },
    [
      editedMessage,
      setNewMessages,
      sendChatMessageEditedAnalyticsEvent,
      editChatMessage,
      editScheduledMessage,
    ]
  );

  const sendMessage = useCallback<SendMessage>(
    (text, linkPreview) => {
      if (!user.email) {
        return;
      }

      const messageParts =
        text.length > MAX_MESSAGE_LENGTH ? splitMessage(text) : [text];

      messageParts.forEach((messageText) => {
        if (editedMessage) {
          editMessage(messageText, linkPreview);
        } else {
          createNewMessage(messageText, linkPreview);
        }
      });
    },
    [user, createNewMessage, editMessage, editedMessage]
  );

  const sendMorpheusMessage = useCallback<SendMorpheusMessage>(
    (text, messageId) => {
      emit({
        event: OUTGOING_EVENT_TYPE.CHAT_MESSAGE_SUMMON_MORPHEUS,
        chat_id: chatId,
        text,
        messageId,
      });
    },
    [emit, chatId]
  );

  const createMessageWithAttachment = useCallback<SendMessageWithAttachment>(
    ({ text, attachments }) => {
      const temporaryMessageId = getUlidId();

      const message: Message = {
        event: OUTGOING_EVENT_TYPE.CHAT_MESSAGE_CREATE,
        chatId: chatId,
        messageId: temporaryMessageId,
        temporaryMessageId: temporaryMessageId,
        text,
        replyTo: parentMessage?.messageId || null,
        replyToMessage: parentMessage,
        createdAt: dateUtils.utc().toISOString(),
        status: MESSAGE_STATUS.CREATED,
        reactions: {},
        attachments,
        type: CHAT_MESSAGE_TYPES.PLAIN,
        content: {
          type: CHAT_MESSAGE_TYPES.PLAIN,
          text,
          attachments,
          linkPreview: null,
        },
        fromUserId: user.userId,
        deleted: false,
      };
      addNewMessage(message);

      const onAttachmentsUploaded = (attachments: Attachment[]) => {
        emit({
          event: OUTGOING_EVENT_TYPE.CHAT_MESSAGE_CREATE,
          chat_id: chatId,
          text,
          temporary_message_id: temporaryMessageId,
          attachments,
          reply_to: parentMessage?.messageId || null,
          ...(draftMessageId
            ? {
                metadata: { draft_message_id: draftMessageId },
              }
            : {}),
        });

        const includesAudio = attachments.some((attachment) =>
          isAudio(attachment.contentType)
        );

        // applied only for audio messages for now
        const [attachment] = attachments;
        const duration = attachment?.duration || 0;

        sendChatMessageSentAnalyticsEvent({
          includesAttachments:
            attachments.length > 0 &&
            attachments.some(
              (attachment) => !isMediaFile(attachment.contentType)
            ),
          includesMedia:
            attachments.length > 0 &&
            attachments.some((attachment) =>
              isMediaFile(attachment.contentType)
            ),
          includesLink: URL_REGEX.test(text),
          includesReply: !!parentMessage,
          includesAudio,
          audioLength: duration > 0 ? formatDuration(duration) : '',
        });

        off('chat_message_attachments_uploaded', onAttachmentsUploaded);
      };
      on('chat_message_attachments_uploaded_failed', () => {
        handleChatMessageFailedUpload(temporaryMessageId);
      });

      on('chat_message_attachments_uploaded', onAttachmentsUploaded);
    },
    [
      chatId,
      user,
      emit,
      addNewMessage,
      handleChatMessageFailedUpload,
      parentMessage,
      sendChatMessageSentAnalyticsEvent,
      draftMessageId,
    ]
  );
  const editMessageWithAttachment = useCallback(
    (
      text: string,
      attachments: UnuploadedAttachment[],
      editedMessage: EditMessageNullable
    ) => {
      const temporaryMessageId = getUlidId();

      setNewMessages((prev) =>
        updateMessage(prev, (msg) =>
          msg.messageId === editedMessage?.messageId
            ? {
                ...msg,
                attachments,
                temporaryMessageId,
                content: {
                  ...msg.content,
                  attachments,
                },
              }
            : null
        )
      );

      const onAttachmentsUploaded = async (attachments: Attachment[]) => {
        await editChatMessage({
          text,
          messageId: editedMessage?.messageId as UUID,
          replyTo: editedMessage?.replyTo!,
          attachments: attachments,
          linkPreview: null,
        });

        off('chat_message_attachments_uploaded', onAttachmentsUploaded);
      };

      on('chat_message_attachments_uploaded_failed', () => {
        handleChatMessageFailedUpload(temporaryMessageId);
      });

      on('chat_message_attachments_uploaded', onAttachmentsUploaded);
    },
    [setNewMessages, editChatMessage, handleChatMessageFailedUpload]
  );

  const sendMessageWithAttachments = useCallback<SendMessageWithAttachment>(
    ({ text, attachments, editedMessage }) => {
      if (!user.email) {
        return;
      }

      if (editedMessage) {
        editMessageWithAttachment(text, attachments, editedMessage);
      } else {
        createMessageWithAttachment({ text, attachments });
      }
    },
    [createMessageWithAttachment, editMessageWithAttachment, user.email]
  );

  const retrySendingMessage = useCallback(
    (id: string) => {
      const failedMessage: Message | undefined = messages
        .filter(({ status }) => status === MESSAGE_STATUS.FAILED)
        .find((message) => message.temporaryMessageId === id) as
        | Message
        | undefined;

      if (!failedMessage) {
        return;
      }

      setNewMessages((prev) =>
        prev.filter((msg) => msg.temporaryMessageId !== id)
      );

      sendMessageWithAttachments({
        text: failedMessage.text,
        attachments: failedMessage.attachments as UnuploadedAttachment[],
      });
    },
    [sendMessageWithAttachments, setNewMessages, messages]
  );

  const deleteMessage = useCallback(
    async ({ messageId }: { messageId: UUID }) => {
      await deleteChatMessage({
        messageId,
      });

      sendChatMessageDeletedAnalyticsEvent();
    },
    [deleteChatMessage, sendChatMessageDeletedAnalyticsEvent]
  );

  const sendReaction = useCallback(
    (messageId: UUID, emoji: string, type: ReactionActionType) => {
      if (!user.email) {
        return;
      }

      const isReactionAdded = type === 'ADD';

      const event = isReactionAdded
        ? OUTGOING_EVENT_TYPE.CHAT_MESSAGE_REACTION_ADD
        : OUTGOING_EVENT_TYPE.CHAT_MESSAGE_REACTION_REMOVE;

      emit({
        event,
        chatId,
        messageId,
        reaction: emoji,
      });

      if (isReactionAdded) {
        sendChatReactionAddedAnalyticsEvent();
      }
    },
    [chatId, user, emit, sendChatReactionAddedAnalyticsEvent]
  );

  const markAsRead = useCallback(
    (inView: boolean, messageId: string) => {
      if (!inView) {
        return;
      }

      emit({
        event: OUTGOING_EVENT_TYPE.CHAT_MESSAGE_SEEN_BY_CLIENT,
        chat_id: chatId,
        message_id: messageId,
      });

      setNewMessages((prev) =>
        prev.map((msg) => {
          if (msg.messageId === messageId) {
            return { ...msg, status: MESSAGE_STATUS.READ } as MessageCreated;
          }

          return msg;
        })
      );

      setUnreadMessagesCount(-1);
    },
    [chatId, emit, setNewMessages, setUnreadMessagesCount]
  );

  const loadReadMessages = useCallback(
    (inView: boolean) => {
      if (inView && hasNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage]
  );

  useEffect(() => {
    on(chatId, handleIncomingMessages);

    return () => {
      off(chatId, handleIncomingMessages);
    };
  }, [chatId, handleIncomingMessages]);

  const providerValue = useMemo(() => {
    const lastReadMessageIndex = messages.findIndex(
      (message) => message.messageId === lastReadMessageId
    );

    return {
      messages,
      isFetching,
      hasFailedMessages,
      sendMessage,
      retrySendingMessage,
      loadReadMessages,
      markAsRead,
      sendReaction,
      sendMessageWithAttachments,
      chatRef,
      lastReadMessageIndex,
      setParentMessage,
      parentMessage,
      fetchNextPage,
      setDraftMessageId,
      draftMessageId,
      editedMessage,
      setEditedMessage,
      deleteMessage,
      sendMorpheusMessage,
      isLoading,
    };
  }, [
    messages,
    isFetching,
    hasFailedMessages,
    sendMessage,
    retrySendingMessage,
    loadReadMessages,
    markAsRead,
    sendReaction,
    sendMessageWithAttachments,
    chatRef,
    lastReadMessageId,
    setParentMessage,
    parentMessage,
    fetchNextPage,
    setDraftMessageId,
    draftMessageId,
    editedMessage,
    setEditedMessage,
    deleteMessage,
    sendMorpheusMessage,
    isLoading,
  ]);

  return (
    <ChatMessagesContext.Provider value={providerValue}>
      {children}
    </ChatMessagesContext.Provider>
  );
};

export const useChatMessagesContext = () => {
  const chatMessagesContext = useContext(ChatMessagesContext);

  if (!chatMessagesContext) {
    throw new Error(
      'useChatMessagesContext must be used in ChatMessagesContext.Provider'
    );
  }

  return chatMessagesContext;
};
